import { IMenuClient } from "@multiweb/clients/menu";
import { getDataReaderClient } from "@multiweb/clients/util";
import { FormOpenMode, IconCode, IFormProfile, IMenuData, IMenuItemData, MenuItemsCollection, MenuItemType, MenuProfilesCollection } from "@multiweb/types/contracts";
import { CaseInsensitiveMap } from "Global/Base/DataTypes/CaseInsensitiveMap";
import { DeepMutable } from "Global/Base/types";
import { ASServices } from "OW.Remoting/Basic/ServiceEndpoints";
import { AttrValueReader } from "OW.Remoting/Remoting/AttrValueReader";
import { RequestContextWithoutTransactionContextId } from "../../../RequestContextWithoutTransactionContextId";
import { BaseClient } from "../BaseClient";

interface ILegacyMenuItems {
    ID: number;
    FormName: string | null;
    HierarchieID: number;
    IconChar: string | null;
    Kod: string | null;
    LocKey: number;
    MenuItemType: LegacyMenuItemType;
    OpenMode: LegacyFormOpenType | null;
}

enum LegacyMenuItemType {
    form = 0, // Formular
    subFolder = 1, // Slozka
    topFolder = 2, // Stranka
}

enum LegacyFormOpenType {
    form = 0,
    paramForm = 1,
    paramFormMethod = 2,
    paramFormReport = 3,
    paramConditionForm = 4,
}

/**
 * Implementace načítání dat pro menu vytvářející zpětnou kompatibilitu pro verze služby <= 15.01.
 * Po zrušení podpory všech verzí <=15.01 tuto implementaci zahodit.
 * @internal
 */
export class LegacyUpTo1501MenuClient extends BaseClient implements IMenuClient {
    public async getMenuItems(requestContext?: RequestContextWithoutTransactionContextId): Promise<IMenuData> {
        const legacyItems: ILegacyMenuItems[] = (await this._communicationManager.callGETRequest("MenuItems", ASServices.owManager, undefined, undefined, requestContext)).menuItems;

        const formCodeToIds = new CaseInsensitiveMap<Set<number>>();

        const items = this._createMenuItems(legacyItems, formCodeToIds);

        const rootItemIds = this._createRootItems(items);

        const legacyProfiles = await this._getUserFormProfiles();
        const profiles = this._addProfilesData(legacyProfiles, items, formCodeToIds);

        return {
            items,
            rootFormItemIds: rootItemIds,
            profiles,
        };
    }

    private _createMenuItems(legacyItems: ILegacyMenuItems[], formCodeToIds: CaseInsensitiveMap<Set<number>>): MenuItemsCollection {
        const items: MenuItemsCollection = {};

        for (const legacyItem of legacyItems) {
            const item = this._createItemData(legacyItem);
            items[item.id] = item;
            let formMenuItemIds = formCodeToIds.get(item.formCode);
            if (!formMenuItemIds)
                formCodeToIds.set(item.formCode, formMenuItemIds = new Set<number>());
            formMenuItemIds.add(item.id);
        }

        return items;
    }

    private _createItemData(legacyItem: ILegacyMenuItems): IMenuItemData {
        return {
            id: legacyItem.ID,
            type: this._legacyItemTypeToItemType(legacyItem.MenuItemType),
            locKey: `XG-${legacyItem.LocKey}`,
            icon: legacyItem.IconChar as IconCode,
            formCode: legacyItem.FormName,
            openMode: this._legacyFormOpenModeToOpenMode(legacyItem.OpenMode),
            code: legacyItem.Kod,
            parent: legacyItem.HierarchieID,
        };
    }

    private _createRootItems(items: MenuItemsCollection): number[] {
        // propojení položek podle hierarchie

        const rootItemIds: number[] = [];

        for (const itemId in items) {
            const item = items[itemId];

            if (item.parent == 0) {
                rootItemIds.push(item.id);
            } else {
                const parent = items[item.parent] as DeepMutable<IMenuItemData>;
                if (!parent)
                    continue;

                if (parent.children == null)
                    parent.children = [];
                parent.children.push(item.id);
            }
        }

        return rootItemIds;
    }

    private _addProfilesData(legacyProfiles: IFormProfile[], items: MenuItemsCollection, formCodeToIds: Map<string, Set<number>>): MenuProfilesCollection {
        const profiles: MenuProfilesCollection = {};

        // všechny profily se napojí do struktury položek menu
        for (const profile of legacyProfiles) {
            const parentIds = formCodeToIds.get(profile.formCode);
            if (!parentIds)
                continue; // profil pro formulář, který už buď neexistuje nebo na něj uživatel nemá práva nebo není v menu

            profiles[profile.id] = profile;

            for (const parentId of parentIds) {
                const parent = items[parentId] as DeepMutable<IMenuItemData>;
                if (!parent)
                    continue;

                if (parent.profiles == null)
                    parent.profiles = [];

                parent.profiles.push(profile.id);
            }
        }

        return profiles;
    }

    private async _getUserFormProfiles(): Promise<IFormProfile[]> {
        const profiles = await getDataReaderClient().getDataRowsWithParams(
            "UserProfile",
            ["ID", "FormName", "Name"],
            ":UserProfile.ActualUserProfiles() AND (DataType = @0) AND ShowInNavigationTree = @1",
            { params: ["FormProfileMW", true], dataLength: -1 });

        return profiles.map(profile => ({
            id: AttrValueReader.read(profile["ID"]),
            title: AttrValueReader.read(profile["Name"]),
            formCode: AttrValueReader.read(profile["FormName"]),
            showInNavigationTree: true,
        }));
    }

    private _legacyItemTypeToItemType(itemTypeId: LegacyMenuItemType): MenuItemType {
        switch (itemTypeId) {
            case LegacyMenuItemType.form:
                return MenuItemType.form;
            case LegacyMenuItemType.topFolder:
            case LegacyMenuItemType.subFolder:
                return MenuItemType.folder;
        }
    }

    private _legacyFormOpenModeToOpenMode(itemTypeId: LegacyFormOpenType): FormOpenMode {
        switch (itemTypeId) {
            case LegacyFormOpenType.form:
                return FormOpenMode.form;
            case LegacyFormOpenType.paramForm:
                return FormOpenMode.paramForm;
            case LegacyFormOpenType.paramFormMethod:
                return FormOpenMode.paramFormMethod;
            case LegacyFormOpenType.paramFormReport:
                return FormOpenMode.paramFormReport;
            case LegacyFormOpenType.paramConditionForm:
                return FormOpenMode.paramConditionForm;
        }
    }
}
